/**
 * Sprites, authored as pixel strings and baked to offscreen canvases once at startup.
 *
 * Nothing here loads a file. The whole art of the game is a few kilobytes of source, which means
 * no sprite sheet to keep in sync, no flash of missing art on a slow connection in Niš, and new
 * islands can be drawn by a function instead of by hand.
 *
 * The character is one body per direction plus a small set of leg blocks that swap under it. The
 * body carries the head, the torso and the arm; the legs carry the gait. Composing the two is why
 * a new pose costs six lines instead of a whole twenty-row sprite, and why the head can never
 * drift out of alignment with the feet.
 */

import type { PropId } from '../../core/content';
import type { Izgled } from '../../core/look';
import * as props from './props';
import { BEARD, EXTRA, HAIR, OUTFIT, type Patch } from './features';
import { palette } from './palette';
import { SOUVENIRS, wearable } from './souvenirs';

export type PixelRows = readonly string[];

export interface SpriteColors {
  [key: string]: string | undefined;
}

const BASE_COLORS: SpriteColors = {
  k: palette.black,
  w: palette.white,
  // The eye. Its own key rather than the outline it used to be, because it is the one pixel on the
  // whole character that a member can choose the colour of and that still reads at portrait scale.
  // Defaulted here so a named character who never mentions eyes gets black ones rather than a hole.
  i: palette.eyes[0],
  // Trousers and boots, near leg then far. The near boot is a good deal lighter than the trousers
  // it hangs off: the walk is read at the feet, and a boot the same value as a trouser leg turns
  // the whole lower half of a sixteen-pixel character into one dark shape that slides.
  p: '#2f4a63',
  P: '#1d3044',
  b: '#7a5433',
  // The far leg and its boot. A stride drawn in one flat colour reads as two front legs on a
  // body with no depth; a step darker and the back leg falls behind the front one.
  B: '#46301c',
  n: palette.trunk,
  N: '#4a2f18',
  g: palette.treeMid,
  G: palette.treeDark,
  l: palette.treeLight,
  r: palette.rock,
  R: palette.rockDark,
  o: palette.wood,
  O: palette.woodDark,
  a: palette.accent,
  A: palette.accentDark,
  y: palette.gold,
  Y: palette.goldDark,
  e: palette.green,
  E: palette.greenDark,
  f: palette.foam,
  d: palette.paper,
  D: palette.paperShade,
  z: palette.warn,
  m: palette.seaDeep,
};

/* ------------------------------------------------------------------ the character */

/**
 * 16 x 20 on a 16 x 16 tile: head rows 1-8, torso 9-13, legs 14-19. The sprite is drawn four
 * pixels above its tile so the soles land exactly on the tile's bottom edge — that edge is the
 * ground line, and every pose below is authored against it.
 *
 * **There is one body, and it has no hair.** There used to be two, and the second one differed from
 * the first only where hair was — the "female body" was a long hairstyle drawn into a body, which
 * is why it could never be worn by anybody else and why every new style would have cost two sets of
 * art. So the hair came off: the scalp here is skin, and `features.ts` puts all ten styles back on
 * top, the first two of which reproduce the two heads this game has always drawn, pixel for pixel.
 *
 * What is deliberately untouched, because souvenirs are authored against it: the crown at rows 1-3,
 * the face at columns 4-11, the neck at columns 5-10 on row 8, and every torso pixel from row 9
 * down. Move any of those and the cap slides off the head.
 */
const BODY_DOWN: PixelRows = [
  '................',
  '.....kkkkkk.....',
  '...kksssssskk...',
  '..kssssssssssk..',
  '..ksssssssssSk..',
  // The two eyes are `i`, not outline. That is the only pixel in the base art that changed value
  // rather than colour, and it is what makes eye colour an axis at all.
  '..ksssississSk..',
  '..ksssssssssSk..',
  '...kssssssssk...',
  '....kssssssk....',
  '....kccccccC....',
  '...kccccccccC...',
  '..skccccccccCs..',
  '..skccccccccCs..',
  '...kccccccccC...',
];

const BODY_UP: PixelRows = [
  '................',
  '.....kkkkkk.....',
  '...kksssssskk...',
  '..kssssssssssk..',
  '..ksssssssssSk..',
  '..ksssssssssSk..',
  '..ksssssssssSk..',
  '...kssssssssk...',
  '....kssssssk....',
  '....kccccccC....',
  '...kccccccccC...',
  '..skccccccccCs..',
  '..skccccccccCs..',
  '...kccccccccC...',
];

const BODY_SIDE: PixelRows = [
  '................',
  '.....kkkkk......',
  '....ksssssk.....',
  '...ksssssssk....',
  // The double outline on the right is the brow and the bridge of the nose. It is the only thing
  // that makes this profile a profile, and no hairstyle may paint over it.
  '...kssssssskk...',
  '...kssssissk....',
  '...kssssssssk...',
  '....kssssssk....',
  '.....kssssk.....',
  '.....kccccC.....',
  '....kcccccC.....',
  '....kccccccC....',
  '...skccccccC....',
  '....kcccccC.....',
];

export type Facing = 'down' | 'up' | 'left' | 'right';

/**
 * The body, by view. `BodyView` and `viewOf` are declared further down, next to the poser that uses
 * them — this record is here only because it wants to sit under the art it indexes.
 */
const BODIES: Record<BodyView, PixelRows> = {
  down: BODY_DOWN,
  up: BODY_UP,
  side: BODY_SIDE,
};

/**
 * One walk cycle is four phases and covers two tiles: pass, contact, pass with the other leg,
 * contact with the other leg. Standing is a fifth pose that only ever shows when nothing is held.
 *
 * The names are the animator's, not the game's: at *pass* the swinging leg goes by the standing
 * one and the body is at its highest, at *contact* the legs are apart and the body is at its
 * lowest. Everything that makes a walk read at this size comes out of that one alternation.
 */
export type HeroPhase = 'stand' | 'passA' | 'contactA' | 'passB' | 'contactB';

/**
 * Where the visible hand sits on the side view. The arm opposes the leg, so it hangs straight
 * down at pass and reaches its extreme at contact — the same pendulum, one phase behind.
 */
type ArmSwing = 'back' | 'down' | 'forward';

const ARMS: Record<ArmSwing, Record<number, string>> = {
  back: { 11: '...skccccccC....', 12: '...skccccccC....', 13: '....kcccccC.....' },
  down: { 11: '....kccccccC....', 12: '....kccccccC....', 13: '...skcccccC.....' },
  // Two pixels tall on the way forward, not one. The torso's left edge is a black outline and its
  // right edge is only a shade darker than the shirt, so a single skin pixel out in front has
  // nothing to sit against and reads as a speck of dirt on the screen rather than as a hand.
  forward: { 11: '....kccccccCs...', 12: '....kccccccCs...', 13: '....kcccccC.....' },
};

/**
 * A pose is its legs, and optionally where the arm is.
 *
 * Six leg rows fill rows 14-19 and leave the body where it was authored. **Seven leg rows push
 * the body up one pixel** and keep the soles on row 19: that is the bob, and it is deliberately
 * built this way rather than as an offset on the draw call. Offsetting the whole sprite lifts the
 * planted foot off the ground along with the head, which is precisely what floating looks like.
 */
interface Pose {
  legs: PixelRows;
  arm?: ArmSwing;
}

/* --- front and back. The stride is a lifted foot and a shift of weight, because a side-on
   stride drawn head-on is just two legs of different lengths. --- */

const FRONT_STAND: PixelRows = [
  '...kppppppppk...',
  '...kppppppppk...',
  '...kppp..pppk...',
  '...kppk..kppk...',
  '...kbbk..kbbk...',
  '...kkkk..kkkk...',
];

// Left foot off the ground, swung in past the standing leg. Seven rows: the body rides up.
const FRONT_PASS_A: PixelRows = [
  '...kppppppppk...',
  '...kppppppppk...',
  '...kppppppppk...',
  '...kppp..pppk...',
  '....kbbk.kppk...',
  '....kkkk.kbbk...',
  '.........kkkk...',
];

const FRONT_PASS_B: PixelRows = [
  '...kppppppppk...',
  '...kppppppppk...',
  '...kppppppppk...',
  '...kppp..pppk...',
  '...kppk.kbbk....',
  '...kbbk.kkkk....',
  '...kkkk.........',
];

// Both feet down, weight on the foot that just landed — one pixel of spread on that boot.
const FRONT_CONTACT_A: PixelRows = [
  '...kppppppppk...',
  '...kppppppppk...',
  '...kppp..pppk...',
  '...kppk..kppk...',
  '..kbbbk..kbbk...',
  '..kkkkk..kkkk...',
];

const FRONT_CONTACT_B: PixelRows = [
  '...kppppppppk...',
  '...kppppppppk...',
  '...kppp..pppk...',
  '...kppk..kppk...',
  '...kbbk..kbbbk..',
  '...kkkk..kkkkk..',
];

/* --- side on. Here the stride is real: the legs scissor open, and the far one is a shade
   darker so the pose reads as depth rather than as a second body. --- */

const SIDE_STAND: PixelRows = [
  '....kpppppk.....',
  '....kpppppk.....',
  '....kpppppk.....',
  '....kPPkppk.....',
  '....kBBkbbk.....',
  '....kkkkkkk.....',
];

/**
 * Legs together, body up, seven rows. The swinging boot ends a row above the standing one — that
 * single pixel is the difference between a stride passing through and a character sliding along
 * on two feet welded to the floor.
 *
 * There are two of them because the foot that lifts here has to be the foot that lands at the
 * next contact. Sharing one pose lifts the back foot and then plants the front one, and while
 * nobody can name what is wrong with that, everybody can see it.
 */
const SIDE_PASS_A: PixelRows = [
  '....kpppppk.....',
  '....kpppppk.....',
  '....kpppppk.....',
  '....kPPpppk.....',
  '....kPPbbbk.....',
  '....kBBkkkk.....',
  '....kkkk........',
];

const SIDE_PASS_B: PixelRows = [
  '....kpppppk.....',
  '....kpppppk.....',
  '....kpppppk.....',
  '....kPPpppk.....',
  '....kBBpppk.....',
  '.....kkbbbk.....',
  '......kkkkk.....',
];

const SIDE_CONTACT_A: PixelRows = [
  '....kpppppk.....',
  '....kpppppk.....',
  '...kPPkkppk.....',
  '..kPPk..kppk....',
  '..kBBk..kbbk....',
  '..kkkk..kkkk....',
];

const SIDE_CONTACT_B: PixelRows = [
  '....kpppppk.....',
  '....kpppppk.....',
  '...kppkkPPk.....',
  '..kppk..kPPk....',
  '..kbbk..kBBk....',
  '..kkkk..kkkk....',
];

const FRONT_POSES: Record<HeroPhase, Pose> = {
  stand: { legs: FRONT_STAND },
  passA: { legs: FRONT_PASS_A },
  contactA: { legs: FRONT_CONTACT_A },
  passB: { legs: FRONT_PASS_B },
  contactB: { legs: FRONT_CONTACT_B },
};

const SIDE_POSES: Record<HeroPhase, Pose> = {
  // Standing shares the arm with pass, so coming to a halt mid-stride never jerks the hand.
  stand: { legs: SIDE_STAND, arm: 'down' },
  passA: { legs: SIDE_PASS_A, arm: 'down' },
  contactA: { legs: SIDE_CONTACT_A, arm: 'back' },
  passB: { legs: SIDE_PASS_B, arm: 'down' },
  contactB: { legs: SIDE_CONTACT_B, arm: 'forward' },
};

/** Overlay merge: a souvenir pixel wins, a '.' keeps whatever the body already has there. */
function overlayRow(base: string, patch: string): string {
  let out = '';
  for (let i = 0; i < base.length; i++) {
    const p = patch[i];
    out += p && p !== '.' ? p : (base[i] ?? '.');
  }
  return out;
}

/**
 * Fourteen rows of body over six or seven rows of legs, always landing on twenty.
 *
 * Souvenirs are painted onto the body rows *before* the slice, in body space — which is the whole
 * trick. The bob drops the body's empty row 0, so a cap authored against body rows 2-4 rides up
 * with the head instead of staying put while the head moves out from under it.
 */
function compose(body: PixelRows, pose: Pose, overlays: ReadonlyArray<Record<number, string>>): string[] {
  const patch = pose.arm ? ARMS[pose.arm] : undefined;
  const from = pose.legs.length === 7 ? 1 : 0;
  const top = body.slice(from, 14).map((row, index) => {
    let out = patch?.[index + from] ?? row;
    for (const overlay of overlays) {
      const deco = overlay[index + from];
      if (deco) out = overlayRow(out, deco);
    }
    return out;
  });
  return [...top, ...pose.legs];
}

const poseCache = new Map<string, string[]>();

/** The three sets of body art. `side` serves both left and right; left is drawn mirrored. */
export type BodyView = 'down' | 'up' | 'side';

export const viewOf = (facing: Facing): BodyView =>
  facing === 'left' || facing === 'right' ? 'side' : facing;

/**
 * Body + legs + any number of overlays, memoised under a caller-supplied key.
 *
 * The key is the caller's job because only the caller knows what makes its sprite different: for
 * the hero it is the souvenirs they are wearing and which body they chose, for a character it is
 * which character. A cache that guesses is a cache that keeps drawing yesterday's sprite.
 *
 * `body` defaults to the first one because the named characters are authored as patches over it —
 * their overlays name rows of *that* head, and handing them the other one would put Marko's fringe
 * on somebody else's hairline.
 */
export function poseRows(
  view: BodyView,
  phase: HeroPhase,
  overlays: ReadonlyArray<Record<number, string>>,
  key: string,
): string[] {
  let rows = poseCache.get(key);
  if (!rows) {
    rows = compose(BODIES[view], view === 'side' ? SIDE_POSES[phase] : FRONT_POSES[phase], overlays);
    poseCache.set(key, rows);
  }
  return rows;
}

/**
 * Which patches go on, and in what order.
 *
 * The order is the whole design. Hair first, because everything else is worn over it. Then the
 * beard, which has to be able to sit on top of a fringe. Then the outfit, which owns the torso and
 * therefore wins wherever long hair reaches the collar — hair falls *behind* a jacket. Then the
 * accessory, so glasses land on a face rather than under a hairstyle. Souvenirs last and always:
 * they were earned, and an earned cap beats a haircut.
 */
function featureOverlays(izgled: Izgled, view: BodyView): Patch[] {
  return [
    HAIR[izgled.hair]!,
    BEARD[izgled.beard]!,
    OUTFIT[izgled.outfit]!,
    EXTRA[izgled.extra]!,
  ].map((feature) => feature[view]);
}

/**
 * Everything about an appearance that changes *rows* rather than colours, as a short string.
 *
 * Colours are deliberately absent: two members with the same haircut and a different hair colour
 * compose to the identical rows and should share one entry in the pose cache. What must never be
 * shared is a bake — `heroLookKey` covers that, and every caller of `bake` combines the two.
 */
const shapeKey = (izgled: Izgled): string =>
  `${izgled.hair}${izgled.beard.toString(16)}${izgled.outfit.toString(16)}${izgled.extra.toString(16)}`;

/**
 * The rows for one pose, plus the cache key the bakery should file them under. Left is right
 * mirrored — one set of side art serves both, so the two can never disagree about the stride.
 *
 * The shape and the souvenirs are both part of the key, for the same reason: they change the rows.
 * The same student five minutes after clearing TEMELJ is a different sprite, and a cache that did
 * not know that would keep drawing them bare-headed until a reload.
 */
export function heroPose(
  facing: Facing,
  phase: HeroPhase,
  izgled: Izgled,
  souvenirs: readonly string[] = [],
): { key: string; rows: string[]; flip: boolean } {
  const view = viewOf(facing);
  const worn = wearable(souvenirs);
  const key = `${shapeKey(izgled)}:${view}:${phase}${worn.length > 0 ? `:${worn.join('+')}` : ''}`;
  const overlays = [...featureOverlays(izgled, view), ...worn.map((id) => SOUVENIRS[id]!.wear[view])];
  return { key, rows: poseRows(view, phase, overlays, key), flip: facing === 'left' };
}

/**
 * Two characters with different hair are two different bakes. The bakery is keyed by string and
 * lives for the life of the tab, so the whole appearance has to be part of every key it files the
 * hero under — otherwise changing your hair repaints nothing until a reload.
 */
export function heroLookKey(izgled: Izgled): string {
  return [
    izgled.skin,
    izgled.hair,
    izgled.hairColor,
    izgled.eyes,
    izgled.beard,
    izgled.outfit,
    izgled.outfitColor,
    izgled.trousers,
    izgled.extra,
  ].join('-');
}

/**
 * Colours only — no rows are decided here.
 *
 * Every shade is computed from its base rather than picked, which is what keeps a palette of a
 * hundred and something combinations coherent: a hand-picked shade for each would be a hundred and
 * something chances to get one wrong, and the one that is wrong is the one somebody is wearing.
 *
 * The three amounts are not interchangeable. Skin gets the gentlest step because a hard shadow on a
 * face at this size reads as dirt; the shirt gets the hardest because that shade is also doing the
 * work of the far arm; hair sits between, except that facial hair needs a value that is neither the
 * hair nor the outline — see `characters.ts` for why that is its own key.
 */
export function heroColors(izgled: Izgled): SpriteColors {
  const skin = palette.skin[izgled.skin % palette.skin.length]!;
  const hair = palette.hair[izgled.hairColor % palette.hair.length]!;
  const shirt = palette.shirt[izgled.outfitColor % palette.shirt.length]!;
  const trousers = palette.trousers[izgled.trousers % palette.trousers.length]!;
  return {
    ...BASE_COLORS,
    s: skin,
    S: shade(skin, -0.18),
    h: hair,
    H: shade(hair, -0.22),
    // A hair highlight. Pale hair reads as grey without one, and a side part reads as a bald patch.
    // Kept close to the base: at +0.3 it stopped being a sheen and became a bald spot, which on a
    // side part is precisely the wrong reading.
    L: shade(hair, 0.16),
    q: shade(hair, -0.12),
    // Stubble is not a thin beard, it is a beard you can see the skin through. Mixing the two is
    // what that is, and mixing them here means it works on every skin tone rather than on one.
    Q: mix(skin, shade(hair, -0.12), 0.55),
    i: palette.eyes[izgled.eyes % palette.eyes.length]!,
    c: shirt,
    C: shade(shirt, -0.25),
    p: trousers,
    // A step darker than the near leg, and it has to stay a step: the stride reads as depth only
    // because the far leg falls behind the near one. See the note on the leg blocks below.
    P: shade(trousers, -0.32),
    // The boots stay the fixed leather from `BASE_COLORS`. They are four pixels at the very bottom
    // of the sprite, and a tenth axis nobody can see the result of is a decision for its own sake.
  };
}

/** How many options each axis has art for. Read by the test that holds it against `core/look`. */
export const FEATURE_COUNTS: Readonly<Record<'hair' | 'beard' | 'outfit' | 'extra', number>> = {
  hair: HAIR.length,
  beard: BEARD.length,
  outfit: OUTFIT.length,
  extra: EXTRA.length,
};


/* ------------------------------------------------------------------ world objects */

/** Lessons. A signpost with the next thing written on it. */
export const SIGN: PixelRows = [
  '................',
  '..kkkkkkkkkkkk..',
  '..kooooooooook..',
  '..kodddddddook..',
  '..kodOOOOOdook..',
  '..kodddddddook..',
  '..kodOOOOOdook..',
  '..kodddddddook..',
  '..kooooooooook..',
  '..kkkkkkkkkkkk..',
  '......kOOk......',
  '......kOOk......',
  '......kOOk......',
  '.....kkOOkk.....',
  '.....kOOOOk.....',
  '.....kkkkkk.....',
];

/** An exercise. A monitor on a stand — the thing you actually work at. */
export const EASEL: PixelRows = [
  '................',
  '.kkkkkkkkkkkkkk.',
  '.kRRRRRRRRRRRRk.',
  '.kRaaaaaaaaaaRk.',
  '.kRaAAAAAAAAaRk.',
  '.kRaAffffffAaRk.',
  '.kRaAfaaaafAaRk.',
  '.kRaAffffffAaRk.',
  '.kRaAAAAAAAAaRk.',
  '.kRaaaaaaaaaaRk.',
  '.kRRRRRRRRRRRRk.',
  '.kkkkkkkkkkkkkk.',
  '......kRRk......',
  '.....kkRRkk.....',
  '....kRRRRRRk....',
  '....kkkkkkkk....',
];

/** The big task. A flag on a mast — visible from across the island, which is the point. */
export const BANNER: PixelRows = [
  '.....kk.........',
  '.....kykkkkkk...',
  '.....kyyyyyyyk..',
  '.....kyYYYYYyk..',
  '.....kyyyyyyyk..',
  '.....kyYYYYYk...',
  '.....kyyyyyk....',
  '.....kykkkk.....',
  '.....kOk........',
  '.....kOk........',
  '.....kOk........',
  '.....kOk........',
  '....kkOkk.......',
  '...kRRRRRk......',
  '...kRRRRRRk.....',
  '...kkkkkkkk.....',
];

/** Treasure — resources that are always there and never a task. */
export const CHEST: PixelRows = [
  '................',
  '................',
  '...kkkkkkkkkk...',
  '..kOOOOOOOOOOk..',
  '..kOyyyyyyyyOk..',
  '..kOyYYYYYYyOk..',
  '..kkkkkkkkkkkk..',
  '..kOOOOkkOOOOk..',
  '..kOyyOkkOyyOk..',
  '..kOyyyyyyyyOk..',
  '..kOyyyyyyyyOk..',
  '..kOOOOOOOOOOk..',
  '..kkkkkkkkkkkk..',
  '................',
  '................',
  '................',
];

/** Done. A stone marker with a cut through it. */
export const MARKER_DONE: PixelRows = [
  '................',
  '................',
  '.....kkkkkk.....',
  '....keeeeeek....',
  '...keeEeeEeek...',
  '...keeeeeeeek...',
  '...keEeeeeEek...',
  '...keeEEEEeek...',
  '....keeeeeek....',
  '.....kkkkkk.....',
  '......kEEk......',
  '.....kkEEkk.....',
  '....kRRRRRRk....',
  '....kkkkkkkk....',
  '................',
  '................',
];

/** The lighthouse on TEMELJ. The first thing you make that sends a signal outward. */
export const LIGHTHOUSE: PixelRows = [
  '.....kkkkkk.....',
  '....kyyyyyyk....',
  '....kyffffyk....',
  '....kyffffyk....',
  '....kyyyyyyk....',
  '...kkkkkkkkkk...',
  '...kdddddddk....',
  '...kdzzzzzdk....',
  '...kdddddddk....',
  '..kddddddddddk..',
  '..kdzzzzzzzzdk..',
  '..kddddddddddk..',
  '..kdzzzzzzzzdk..',
  '.kddddddddddddk.',
  '.kRRRRRRRRRRRRk.',
  '.kkkkkkkkkkkkkk.',
];

/* ------------------------------------------------------------------ props */

/**
 * Things the world builder can put on an island purely because they make it a place.
 *
 * None of them is solid, and that is a rule rather than an oversight: terrain blocks, objects
 * never do. It means no arrangement of decorations can ever wall a student away from their next
 * step, so the reachability check only has to think about tiles. Want a wall? Paint a fence.
 */
const CAMPFIRE_LOGS: PixelRows = [
  '...knnkkknnk....',
  '..knnnnnnnnnk...',
  '..kNNNNNNNNNk...',
  '..kkkkkkkkkkk...',
];

export const CAMPFIRE_A: PixelRows = [
  '................',
  '................',
  '................',
  '.......z........',
  '......zyz.......',
  '......zyz.......',
  '.....zywyz......',
  '.....zyyyz......',
  '....zzyyyzz.....',
  '.....zzzzz......',
  '................',
  ...CAMPFIRE_LOGS,
  '................',
];

export const CAMPFIRE_B: PixelRows = [
  '................',
  '................',
  '................',
  '................',
  '......z.........',
  '.....zyz........',
  '.....zywz.......',
  '.....zyyz.......',
  '....zzyyyz......',
  '.....zzzzz......',
  '................',
  ...CAMPFIRE_LOGS,
  '................',
];

export const BARREL: PixelRows = [
  '................',
  '................',
  '................',
  '................',
  '.....kkkkkk.....',
  '....koooooOk....',
  '....koOOOOOk....',
  '....kooooook....',
  '....kOOOOOOk....',
  '....kooooook....',
  '....koOOOOOk....',
  '....kooooook....',
  '....kOOOOOOk....',
  '.....kkkkkk.....',
  '................',
  '................',
];

export const CRATE: PixelRows = [
  '................',
  '................',
  '................',
  '...kkkkkkkkkk...',
  '...kOooooooOk...',
  '...koOoooooOk...',
  '...kooOoooOok...',
  '...koooOoOook...',
  '...kooooOoook...',
  '...koooOoOook...',
  '...kooOoooOok...',
  '...koOoooooOk...',
  '...kOooooooOk...',
  '...kkkkkkkkkk...',
  '................',
  '................',
];

/** A noticeboard. Where a community would put its notes up, which is what this one is about. */
export const NOTICEBOARD: PixelRows = [
  '................',
  '..kkkkkkkkkkkk..',
  '..koooooooooOk..',
  '..koddddddddok..',
  '..kodDDDDDDdok..',
  '..koddddddddok..',
  '..kodDDDDDDdok..',
  '..koddddddddok..',
  '..koOOOOOOOOOk..',
  '..kkkkkkkkkkkk..',
  '.....kOk.kOk....',
  '.....kOk.kOk....',
  '.....kOk.kOk....',
  '....kkOkkkOkk...',
  '....kOOOOOOOk...',
  '....kkkkkkkkk...',
];

export const PALM: PixelRows = [
  '................',
  '....kk....kk....',
  '...kllkkkkllk...',
  '..kllllglllllk..',
  '..klllgggglllk..',
  '...kkkgnngkkk...',
  '......knnk......',
  '......knnk......',
  '.....knnk.......',
  '.....knnk.......',
  '....knnk........',
  '....knnk........',
  '....knnk........',
  '...kknnkk.......',
  '...kNNNNk.......',
  '...kkkkkk.......',
];

/** The boat you cross open water in. */
export const BOAT: PixelRows = [
  '................',
  '................',
  '................',
  '................',
  '.......k........',
  '......kdk.......',
  '......kddk......',
  '......kdddk.....',
  '......kddddk....',
  '......kOk.......',
  '.kkkkkkOkkkkkk..',
  '.kOOOOOOOOOOOk..',
  '.kONNNNNNNNNOk..',
  '..kOOOOOOOOOk...',
  '...kkkkkkkkk....',
  '................',
];

/* ------------------------------------------------------------------ baking */

export interface Baked {
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
}

const bakeCache = new Map<string, Baked>();

/**
 * How many finished canvases the bakery keeps.
 *
 * It used to keep everything, and that was fine while a character was one of eight: the whole map
 * plus every pose of every peer came to a few hundred entries and stayed there for the life of the
 * tab. The wardrobe changed the arithmetic — a hundred and sixty million appearances, each of which
 * bakes a preview and about thirty thumbnails as somebody scrolls through it — so an unbounded map
 * of canvases is a phone running out of memory in a screen whose whole job is to be played with.
 *
 * Five hundred and twelve is comfortably more than a map full of people needs at once, and eviction
 * is oldest-first rather than a wholesale clear: dropping everything would take the tiles and the
 * hero down with the thumbnails and re-bake the visible frame from scratch.
 */
const BAKE_LIMIT = 512;

export function bake(
  key: string,
  rows: PixelRows,
  colors: SpriteColors = BASE_COLORS,
  scale = 1,
  flip = false,
): Baked {
  const cacheKey = `${key}|${scale}|${flip ? 'f' : 'n'}`;
  const cached = bakeCache.get(cacheKey);
  if (cached) return cached;

  const width = (rows[0]?.length ?? 0) * scale;
  const height = rows.length * scale;
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, width);
  canvas.height = Math.max(1, height);
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;

  for (let y = 0; y < rows.length; y++) {
    const row = rows[y]!;
    let runStart = -1;
    let runColor: string | undefined;
    for (let x = 0; x <= row.length; x++) {
      const color = x < row.length ? colors[row[x]!] : undefined;
      if (color !== runColor) {
        if (runColor && runStart >= 0) {
          ctx.fillStyle = runColor;
          const drawX = flip ? row.length - x : runStart;
          ctx.fillRect(drawX * scale, y * scale, (x - runStart) * scale, scale);
        }
        runStart = x;
        runColor = color;
      }
    }
  }

  const baked = { canvas, width: canvas.width, height: canvas.height };
  if (bakeCache.size >= BAKE_LIMIT) {
    // A Map iterates in insertion order, so the first key is the least recently *baked*. Close
    // enough to least recently used for something whose miss costs one fill of a 16x20 canvas.
    const oldest = bakeCache.keys().next().value;
    if (oldest !== undefined) bakeCache.delete(oldest);
  }
  bakeCache.set(cacheKey, baked);
  return baked;
}

export function clearBakeCache(): void {
  bakeCache.clear();
}

export const spriteColors = BASE_COLORS;

/* ------------------------------------------------------------------ the prop registry */

/*
 * The furniture lives in its own file. Twenty-three sprites of sofa-and-lamp would double the length
 * of this one, and nothing in here needs to know about them — the registry below is the only seam.
 */

export interface PropArt {
  /** More than one frame animates on the world clock; the campfire is the only one that does. */
  frames: PixelRows[];
  /**
   * How far above its own tile the sprite is drawn. A lighthouse standing inside a single tile is
   * a lamp on the ground; lifting it is the entire difference between a prop and a landmark.
   */
  lift: number;
  /** Serbian, for the builder's palette. */
  label: string;
}

export const PROPS: Record<PropId, PropArt> = {
  svetionik: { frames: [LIGHTHOUSE], lift: 16, label: 'svetionik' },
  vatra: { frames: [CAMPFIRE_A, CAMPFIRE_B], lift: 0, label: 'vatra' },
  bure: { frames: [BARREL], lift: 0, label: 'bure' },
  sanduk: { frames: [CRATE], lift: 0, label: 'sanduk' },
  tabla: { frames: [NOTICEBOARD], lift: 6, label: 'tabla' },
  barka: { frames: [BOAT], lift: 0, label: 'barka' },
  palma: { frames: [PALM], lift: 8, label: 'palma' },

  /**
   * The furniture, for a member arranging a place they live in.
   *
   * `lift` is the whole art direction here: anything that should read as standing up rather than
   * lying on the ground is drawn above its own tile. A lamp at lift 0 is a lamp somebody dropped.
   */
  stolica: { frames: [props.CHAIR], lift: 2, label: 'stolica' },
  sto: { frames: [props.TABLE], lift: 2, label: 'sto' },
  'radni-sto': { frames: [props.DESK], lift: 6, label: 'radni sto' },
  polica: { frames: [props.BOOKSHELF], lift: 10, label: 'polica' },
  lampa: { frames: [props.LAMP], lift: 12, label: 'lampa' },
  fenjer: { frames: [props.LANTERN], lift: 10, label: 'fenjer' },
  klupa: { frames: [props.BENCH], lift: 2, label: 'klupa' },
  suncobran: { frames: [props.UMBRELLA], lift: 12, label: 'suncobran' },
  ljuljaska: { frames: [props.HAMMOCK], lift: 4, label: 'ljuljaška' },
  satar: { frames: [props.TENT], lift: 6, label: 'šator' },
  bunar: { frames: [props.WELL], lift: 8, label: 'bunar' },
  saksija: { frames: [props.PLANTER], lift: 2, label: 'saksija' },
  panj: { frames: [props.STUMP], lift: 0, label: 'panj' },
  pecurka: { frames: [props.MUSHROOM], lift: 0, label: 'pečurka' },
  skoljka: { frames: [props.SHELL], lift: 0, label: 'školjka' },
  bova: { frames: [props.BUOY], lift: 2, label: 'bova' },
  mreze: { frames: [props.NETS], lift: 0, label: 'mreže' },
  kip: { frames: [props.STATUE], lift: 10, label: 'kip' },
  merdevine: { frames: [props.LADDER], lift: 10, label: 'merdevine' },
  bubanj: { frames: [props.DRUM], lift: 2, label: 'bubanj' },
  gajbe: { frames: [props.CRATES], lift: 4, label: 'gajbe' },
  stub: { frames: [props.POST], lift: 8, label: 'stub' },
  zastava: { frames: [props.FLAG], lift: 12, label: 'zastava' },
};

export const propArt = (id: string): PropArt | undefined =>
  (PROPS as Record<string, PropArt>)[id];

/* ------------------------------------------------------------------ colour helpers */

/**
 * Two colours, blended. Used for exactly one thing — stubble, which is a beard you can see the skin
 * through, and therefore cannot be a fixed colour without being wrong on six of the eight tones.
 */
export function mix(a: string, b: string, weight: number): string {
  const read = (hex: string) => {
    const value = hex.replace('#', '');
    return parseInt(value.length === 3 ? value.replace(/./g, (c) => c + c) : value, 16);
  };
  const left = read(a);
  const right = read(b);
  const channel = (shift: number) => {
    const from = (left >> shift) & 0xff;
    const to = (right >> shift) & 0xff;
    return Math.max(0, Math.min(255, Math.round(from + (to - from) * weight)));
  };
  return `#${[channel(16), channel(8), channel(0)]
    .map((c) => c.toString(16).padStart(2, '0'))
    .join('')}`;
}

export function shade(hex: string, amount: number): string {
  const value = hex.replace('#', '');
  const num = parseInt(value.length === 3 ? value.replace(/./g, (c) => c + c) : value, 16);
  const channel = (shift: number) => {
    const base = (num >> shift) & 0xff;
    const next = amount >= 0 ? base + (255 - base) * amount : base * (1 + amount);
    return Math.max(0, Math.min(255, Math.round(next)));
  };
  return `#${[channel(16), channel(8), channel(0)]
    .map((c) => c.toString(16).padStart(2, '0'))
    .join('')}`;
}
